import type { Guide, GuideFormData } from '../types/guides';

/**
 * Convert a guide to Markdown format
 */
export const guideToMarkdown = (guide: Guide): string => {
  const lines: string[] = [];
  
  // Add title as H1
  lines.push(`# ${guide.metadata.title}`);
  lines.push('');
  
  // Add metadata as front matter (optional YAML-style)
  lines.push('---');
  lines.push(`name: ${guide.metadata.name}`);
  lines.push(`category: ${guide.metadata.category || 'Uncategorized'}`);
  if (guide.metadata.tags && guide.metadata.tags.length > 0) {
    lines.push(`tags: ${guide.metadata.tags.join(', ')}`);
  }
  if (guide.metadata.version) {
    lines.push(`version: ${guide.metadata.version}`);
  }
  if (guide.metadata.lastUpdated) {
    lines.push(`lastUpdated: ${new Date(guide.metadata.lastUpdated).toISOString()}`);
  }
  lines.push('---');
  lines.push('');
  
  // Add description if present
  if (guide.metadata.description) {
    lines.push(`> ${guide.metadata.description}`);
    lines.push('');
  }
  
  // Add the main content
  lines.push(guide.content);
  
  return lines.join('\n');
};

/**
 * Download a guide as a Markdown file
 */
export const downloadGuideAsMarkdown = (guide: Guide): void => {
  const markdown = guideToMarkdown(guide);
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  // Create a temporary anchor element to trigger download
  const a = document.createElement('a');
  a.href = url;
  a.download = `${guide.metadata.name || 'guide'}.md`;
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export multiple guides as a single Markdown file
 */
export const downloadGuidesAsMarkdown = (guides: Guide[], filename: string = 'guides'): void => {
  const markdownSections = guides.map((guide, index) => {
    const markdown = guideToMarkdown(guide);
    // Add separator between guides (except for the last one)
    return index < guides.length - 1 ? markdown + '\n\n---\n\n' : markdown;
  });
  
  const fullContent = markdownSections.join('');
  const blob = new Blob([fullContent], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Parse Markdown content to extract guide data
 */
export const parseMarkdownToGuide = (markdownContent: string): Partial<GuideFormData> => {
  const lines = markdownContent.split('\n');
  const guideData: Partial<GuideFormData> = {};
  
  let inFrontMatter = false;
  let contentStartIndex = 0;
  
  // Parse the title from H1
  const titleMatch = lines[0]?.match(/^#\s+(.+)$/);
  if (titleMatch) {
    guideData.title = titleMatch[1].trim();
    contentStartIndex = 1;
  }
  
  // Parse front matter if present
  for (let i = contentStartIndex; i < lines.length; i++) {
    const line = lines[i];
    
    if (line === '---') {
      if (!inFrontMatter) {
        inFrontMatter = true;
        contentStartIndex = i + 1;
        continue;
      } else {
        // End of front matter
        contentStartIndex = i + 1;
        break;
      }
    }
    
    if (inFrontMatter) {
      // Parse YAML-style front matter
      const match = line.match(/^(\w+):\s*(.+)$/);
      if (match) {
        const [, key, value] = match;
        switch (key.toLowerCase()) {
          case 'name':
            guideData.name = value.trim();
            break;
          case 'category':
            guideData.category = value.trim() === 'Uncategorized' ? undefined : value.trim();
            break;
          case 'tags':
            guideData.tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
            break;
          case 'version':
            // Skip version as it's managed by the system
            break;
          case 'lastupdated':
            // Skip lastUpdated as it's managed by the system
            break;
        }
      }
    }
  }
  
  // Check for description (blockquote after front matter)
  let descriptionIndex = contentStartIndex;
  if (lines[contentStartIndex]?.trim() === '' && lines[contentStartIndex + 1]?.startsWith('>')) {
    descriptionIndex = contentStartIndex + 1;
    const descriptionMatch = lines[descriptionIndex].match(/^>\s*(.+)$/);
    if (descriptionMatch) {
      guideData.description = descriptionMatch[1].trim();
      contentStartIndex = descriptionIndex + 1;
    }
  }
  
  // Skip empty lines after description
  while (contentStartIndex < lines.length && lines[contentStartIndex].trim() === '') {
    contentStartIndex++;
  }
  
  // The rest is content
  guideData.content = lines.slice(contentStartIndex).join('\n').trim();
  
  // Generate a name from title if not provided
  if (!guideData.name && guideData.title) {
    guideData.name = guideData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
  
  return guideData;
};

/**
 * Import guide from file
 */
export const importGuideFromFile = (file: File): Promise<Partial<GuideFormData>> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const guideData = parseMarkdownToGuide(content);
        resolve(guideData);
      } catch (error) {
        reject(new Error('Failed to parse markdown file'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};